/**
 * Token persistence for the SPA.
 *
 * We hold Cognito-issued tokens in localStorage under a single key so that
 * (a) sessions survive page reloads, and (b) the AuthProvider can detect
 * "user is still signed in" on mount without a network round trip.
 *
 * localStorage is XSS-vulnerable; the alternative (HttpOnly cookies set by
 * a backend) requires a backend session layer we don't have. The same
 * trade-off Amplify and oidc-client-ts both make. Mitigate by enforcing
 * strict CSP later and minimising third-party scripts.
 */

export interface StoredTokens {
  readonly accessToken: string;
  readonly idToken: string;
  readonly refreshToken: string;
  /** Epoch milliseconds. */
  readonly expiresAt: number;
}

const STORAGE_KEY = 'mailchute.auth.tokens';

export function loadTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTokens>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.idToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.expiresAt !== 'number'
    ) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      idToken: parsed.idToken,
      refreshToken: parsed.refreshToken,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    // Quota errors, JSON.parse errors, localStorage disabled, etc. — treat
    // as "no stored session" rather than crashing the app.
    return null;
  }
}

export function saveTokens(tokens: StoredTokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Treat a token as needing refresh when it has less than this many ms of
 * life remaining. Gives us headroom on slow networks and clock skew.
 */
const REFRESH_THRESHOLD_MS = 60_000;

export function isNearExpiry(tokens: StoredTokens, now: number = Date.now()): boolean {
  return tokens.expiresAt - now < REFRESH_THRESHOLD_MS;
}
